import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice, Pager, Stat } from "../../lib/ui";

/*
 * Transaction entries – port of accountBalanceL.php (API: GET /api/transactions/entries, backend/ledger.py)
 * Filters (search, from/to, status), sortable date/debit/credit/status, 10–100 rows per page, page totals, CSV export.
 * Dates default to today, as in PHP.
 */

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => new Date(d.replace(" ", "T")).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

export default function TransactionEntries() {
  const api = useApi();
  const [f, setF] = useState({ search: "", from: today(), to: today(), status: "", sort: "transaction_date", order: "DESC", page: 1, limit: 10 });
  const [form, setForm] = useState(f);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async (q) => {
    try { setData(await api.get("/api/transactions/entries", q)); setError(null); }
    catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(f); }, [load, f]);

  const sortBy = (col) => setF((s) => ({ ...s, sort: col, order: s.sort === col && s.order === "DESC" ? "ASC" : "DESC", page: 1 }));
  const Th = ({ col, children, num }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => sortBy(col)} aria-sort={f.sort === col ? (f.order === "ASC" ? "ascending" : "descending") : "none"}>
      {children} {f.sort === col && <i className={`fas ${f.order === "ASC" ? "fa-sort-up" : "fa-sort-down"}`} />}
    </th>
  );
  const csvUrl = () => {
    const u = new URL(api.base + "/api/transactions/entries");
    ["search", "from", "to", "status", "sort", "order"].forEach((k) => u.searchParams.set(k, f[k]));
    u.searchParams.set("export", "csv");
    return u.toString();
  };

  return (
    <main className="pg">
      <PageHeader slug="accountBalanceL" actions={<a className="btn" href={csvUrl()}><i className="fas fa-file-csv" /> Export CSV</a>} />
      <Notice notice={error && { tone: "err", text: error }} />
      {data && (
        <div className="ui-stats">
          <Stat icon="fa-list" label="Total records" value={data.total.toLocaleString("en-US")} />
          <Stat icon="fa-file-lines" tone="muted" label="Page" value={`${data.page}/${data.pages}`} />
        </div>
      )}
      <section className="panel">
        <form className="toolbar" onSubmit={(e) => { e.preventDefault(); setF({ ...f, ...form, page: 1 }); }}>
          <div className="field grow"><label htmlFor="te-q">Search</label>
            <input id="te-q" value={form.search} onChange={(e) => setForm({ ...form, search: e.target.value })} placeholder="Account name or number" /></div>
          <div className="field"><label htmlFor="te-from">From</label><input id="te-from" type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>
          <div className="field"><label htmlFor="te-to">To</label><input id="te-to" type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>
          <div className="field"><label htmlFor="te-st">Status</label>
            <select id="te-st" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="">All statuses</option><option>Completed</option><option>Pending</option>
            </select></div>
          <button type="submit" className="btn primary">Apply</button>
          <div className="field" style={{ minWidth: "7rem" }}><label htmlFor="te-lim">Show</label>
            <select id="te-lim" value={f.limit} onChange={(e) => setF({ ...f, limit: Number(e.target.value), page: 1 })}>
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select></div>
        </form>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr>
              <Th col="transaction_date">Date</Th><th>Account no.</th><th>Account name</th>
              <Th col="total_debit" num>Debit</Th><Th col="total_credit" num>Credit</Th><Th col="status">Status</Th>
              <th>Payment ID</th><th>Invoice ID</th>
            </tr></thead>
            <tbody>
              {data?.rows.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDate(r.date)}</td><td className="mono">{r.account_number || "–"}</td><td>{r.account_name || "–"}</td>
                  <td className="num mono">{r.debit > 0 ? n2(r.debit) : "–"}</td><td className="num mono">{r.credit > 0 ? n2(r.credit) : "–"}</td>
                  <td><span className={`pill-tag ${r.status === "Completed" ? "ok" : r.status === "Pending" ? "warn" : ""}`}>{r.status}</span></td>
                  <td className="mono">{r.payment_id ?? "–"}</td><td className="mono">{r.invoice_id ?? "–"}</td>
                </tr>
              ))}
            </tbody>
            {data?.rows.length > 0 && (
              <tfoot><tr><td colSpan={3}>Page totals</td><td className="num mono">{n2(data.page_debit)}</td><td className="num mono">{n2(data.page_credit)}</td><td colSpan={3} /></tr></tfoot>
            )}
          </table>
          {data && data.rows.length === 0 && <div className="empty"><i className="fas fa-inbox" />No transactions found for these filters.</div>}
        </div>
        {data && <Pager page={data.page} totalPages={data.pages} onPage={(p) => setF({ ...f, page: p })} />}
      </section>
    </main>
  );
}
